import posthog from 'posthog-js';
import { browserErrorDiagnostic } from './browserErrorDiagnostic';

interface BrowserPostHogConfig {
  key?: string;
  host?: string;
  environment?: string;
}

let initialized = false;
let exceptionListenersAttached = false;
const REPLAY_BLOCKED_ROUTES = new Set(['/onboarding', '/reference/respond']);
const REPLAY_RENDERING_ATTRIBUTES = new Set([
  'class',
  'type',
  'width',
  'height',
  'hidden',
  'open',
  'disabled',
  'checked',
  'selected',
  'multiple',
  'rows',
  'cols',
  'colspan',
  'rowspan',
  'size',
  'min',
  'max',
  'step',
  'viewbox',
  'preserveaspectratio',
  'd',
  'pathlength',
  'fill',
  'fill-rule',
  'clip-rule',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
  'stroke-dashoffset',
  'opacity',
  'transform',
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'points',
]);
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const REPLAY_TEXT_REDACTIONS = [
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
  /\b\d{3}-\d{2}-\d{4}\b/g,
  /(?:\+?1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]\d{3}[\s.-]\d{4}\b/g,
  /\bBearer\s+[A-Za-z0-9._~+/-]+=*\b/gi,
  /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g,
  /([?&](?:access_token|auth|code|invite_token|jwt|key|secret|token)=)[^&#\s]+/gi,
];

function withoutQueryOrHash(value: string): string {
  try {
    const url = new URL(value);
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return value;
  }
}

function browserConfig(): BrowserPostHogConfig {
  return {
    key: import.meta.env.VITE_POSTHOG_KEY,
    host: import.meta.env.VITE_POSTHOG_HOST,
    environment: import.meta.env.VITE_APP_ENVIRONMENT,
  };
}

function maskReplayText(value: string): string {
  return REPLAY_TEXT_REDACTIONS.reduce(
    (redacted, pattern) =>
      redacted.replace(pattern, (match, prefix?: string) => {
        if (typeof prefix === 'string' && match.startsWith(prefix)) {
          return `${prefix}${'*'.repeat(match.length - prefix.length)}`;
        }
        return '*'.repeat(match.length);
      }),
    value,
  );
}

function sanitizeReplayCss(value: string): string {
  return value
    .replace(/@import\s+[^;]+;?/gi, '')
    .replace(/url\s*\([^)]*\)/gi, 'url("")')
    .replace(/content\s*:[^;}{]+/gi, 'content:""');
}

function maskReplayAttribute(name: string, value: string, element?: Element): string {
  const attribute = name.toLowerCase();
  const tagName = element?.tagName?.toLowerCase();
  const isStylesheetLink =
    tagName === 'link' && element?.getAttribute('rel') === 'stylesheet';
  if (isStylesheetLink && attribute === 'rel') return value;
  if (isStylesheetLink && attribute === 'href' && element) {
    const origin = element.ownerDocument.location.origin;
    try {
      const url = new URL(value, origin);
      if (url.origin === origin && !url.search && !url.hash) return url.toString();
    } catch {
      // Invalid stylesheet references are masked below.
    }
  }
  if (attribute === 'style' || attribute === '_csstext') {
    // rrweb serializes compiled linked stylesheets into a synthetic `_cssText`
    // attribute. Preserve their rendering rules while stripping external/data
    // URLs, imports, and generated textual content.
    return sanitizeReplayCss(value);
  }
  if (REPLAY_RENDERING_ATTRIBUTES.has(attribute)) return value;
  if (element?.namespaceURI === SVG_NAMESPACE) {
    if (attribute === 'id') return value;
    if ((attribute === 'href' || attribute === 'xlink:href') && value.startsWith('#')) return value;
  }
  return '*'.repeat(value.length);
}

function attachExceptionListeners(): void {
  if (exceptionListenersAttached || typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    const buildSha = typeof document === 'undefined'
      ? undefined
      : document.querySelector<HTMLMetaElement>('meta[name="build-sha"]')?.content;
    const { exception, properties } = browserErrorDiagnostic(event, window.location?.origin, buildSha);
    posthog.captureException(exception, properties);
  });
  window.addEventListener('unhandledrejection', (event) => {
    posthog.captureException(event.reason);
  });
  exceptionListenersAttached = true;
}

export function initializeBrowserPostHog(config = browserConfig()): boolean {
  const key = config.key?.trim();
  if (!key || initialized || posthog.__loaded) return false;

  posthog.init(key, {
    api_host: config.host?.trim() || 'https://us.i.posthog.com',
    autocapture: false,
    // Use stable event listeners below rather than the SDK's assignable
    // window.onerror wrapper, which other browser tooling can replace.
    capture_exceptions: false,
    capture_performance: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    enable_recording_console_log: false,
    person_profiles: 'never',
    session_recording: {
      // Preserve ordinary UI copy for useful replays while redacting recognized
      // PII substrings and all form values.
      maskAllInputs: true,
      // Apply the selective text function to visible body copy. Exclude
      // style/script elements so stylesheets and application code are untouched.
      maskTextSelector: 'body, body *:not(style):not(script)',
      maskTextFn: maskReplayText,
      maskAttributeFn: maskReplayAttribute,
      blockSelector: 'img, video, audio, canvas, iframe, [data-ph-no-capture]',
      collectFonts: false,
      recordBody: false,
      recordCrossOriginIframes: false,
      recordHeaders: false,
    },
    before_send: (event) => {
      if (!event?.properties) return event;
      for (const key of ['$current_url', '$referrer', '$initial_referrer']) {
        const value = event.properties[key];
        if (typeof value === 'string') event.properties[key] = withoutQueryOrHash(value);
      }
      return event;
    },
  });
  posthog.register({
    source: 'ppp2-portal',
    ...(config.environment ? { environment: config.environment } : {}),
  });
  attachExceptionListeners();
  initialized = true;
  return true;
}

export function captureBrowserPageView(currentUrl = window.location.href): boolean {
  if (!initialized && !posthog.__loaded) return false;
  posthog.capture('$pageview', { $current_url: withoutQueryOrHash(currentUrl) });
  return true;
}

export function updateBrowserSessionRecording(pathname: string): boolean {
  if (!initialized && !posthog.__loaded) return false;
  // These public routes carry bearer-like tokens in their URLs. Keep their
  // sanitized pageviews but never initialize rrweb on the tokenized document.
  if (REPLAY_BLOCKED_ROUTES.has(pathname)) {
    posthog.stopSessionRecording();
  } else {
    // Browser sessions are a product requirement. Explicitly override remote
    // sampling/linked-flag gates while retaining all local privacy controls.
    posthog.startSessionRecording(true);
  }
  return true;
}
